import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { type EpisodeList, type Product } from '@/api/types/product'
import { Header } from '@/components/shared/header'
import { useGoBack } from '@/hooks/useGoBack'
import { getAllOfflineEpisodes, type OfflineEpisode } from '@/lib/db'
import { MUSIC_DOMAIN } from '@/pages/music/constants'

import { ComicItem } from './products/components/comic-item'
import { EpisodeDrawer } from './products/components/episode-drawer'
import { GridLayout } from './products/components/grid-layout'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** OfflineEpisode → EpisodeList 変換（EpisodeDrawer / ComicItem 用） */
const toEpisodeList = (ep: OfflineEpisode, index: number): EpisodeList => ({
  episode_id: ep.episodeId,
  domain_episode_id: String(ep.episodeId),
  created_at: new Date(ep.downloadedAt).toISOString(),
  index,
  num_pages: 0,
  upload_date: new Date(ep.downloadedAt).toISOString(),
  description: null,
  mime_type: ep.mimeType,
  custom_name: ep.title,
  file_size: ep.fileSize,
  thumbnail_url: null,
  progress: null,
  is_completed: false,
  is_favorite: false,
})

/**
 * オフラインエピソードをプロダクト単位でグルーピングし、
 * ComicItem 互換の Product 型に変換する。
 * thumbnailBlob は別途 ObjectURL に変換して thumbnail に設定する。
 */
const buildOfflineProducts = (episodes: OfflineEpisode[], thumbnailUrls: Map<number, string>): Product[] => {
  const map = new Map<number, OfflineEpisode[]>()
  for (const ep of episodes) {
    const group = map.get(ep.productId) ?? []
    group.push(ep)
    map.set(ep.productId, group)
  }

  return Array.from(map.entries()).map(([productId, eps]) => ({
    product_id: productId,
    domain: eps[0].domain,
    domain_product_id: String(productId),
    title: eps[0].title,
    thumbnail: thumbnailUrls.get(productId) ?? null,
    favorite_episode_count: 0,
    first_episode_id: eps[0]?.episodeId ?? null,
    description: null,
    tags: [],
    episodes: eps.map((ep, i) => toEpisodeList(ep, i)),
  }))
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const OfflineProducts = () => {
  const navigate = useNavigate()
  const goBack = useGoBack()
  const [rawEpisodes, setRawEpisodes] = useState<OfflineEpisode[]>([])
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // IndexedDB からオフラインエピソードを取得
  const loadEpisodes = useCallback(async () => {
    const episodes = await getAllOfflineEpisodes()
    // 音楽はダウンロード済みライブラリ（/music/downloaded）で扱うため、ここには出さない
    setRawEpisodes(episodes.filter((ep) => ep.domain !== MUSIC_DOMAIN))
  }, [])

  useEffect(() => {
    loadEpisodes()
  }, [loadEpisodes])

  // サムネイル blob → ObjectURL マップ（メモリリーク防止付き）
  const thumbnailUrls = useMemo(() => {
    const map = new Map<number, string>()
    const seen = new Set<number>()
    for (const ep of rawEpisodes) {
      if (seen.has(ep.productId)) continue
      seen.add(ep.productId)
      if (ep.thumbnailBlob) {
        map.set(ep.productId, URL.createObjectURL(ep.thumbnailBlob))
      }
    }
    return map
  }, [rawEpisodes])

  useEffect(() => {
    return () => {
      for (const url of thumbnailUrls.values()) {
        URL.revokeObjectURL(url)
      }
    }
  }, [thumbnailUrls])

  // Product 型に変換（ComicItem 互換）
  const products = useMemo(() => buildOfflineProducts(rawEpisodes, thumbnailUrls), [rawEpisodes, thumbnailUrls])

  const handleProductClick = useCallback(
    (productId: number) => {
      const product = products.find((p) => p.product_id === productId)
      if (!product) return

      // エピソードが 1 件だけなら直接リーダーへ
      if (product.episodes.length === 1) {
        navigate(`/p/${product.episodes[0].episode_id}`)
        return
      }

      setSelectedProduct(product)
      setDrawerOpen(true)
    },
    [products, navigate],
  )

  const handleEpisodeSelect = useCallback(
    (episodeId: number) => {
      navigate(`/p/${episodeId}`)
    },
    [navigate],
  )

  const handleDrawerClose = useCallback((open: boolean) => {
    setDrawerOpen(open)
    if (!open) setSelectedProduct(null)
  }, [])

  const renderItem = useCallback(
    (index: number) => {
      const product = products[index]
      if (!product) return null
      return (
        <ComicItem
          target={product}
          domain={product.domain}
          index={index}
          isVideoDomain={false}
          onItemClick={handleProductClick}
        />
      )
    },
    [products, handleProductClick],
  )

  return (
    <div className="fixed inset-0 flex flex-col">
      <Header title="ダウンロード済み" onClose={goBack} />

      {products.length === 0 ? (
        <div className="flex flex-col items-center justify-center flex-1 px-6 text-center">
          <div className="text-muted-foreground mb-2">ダウンロード済みのコンテンツがありません</div>
          <div className="text-sm text-muted-foreground">
            プロダクトページからエピソードをダウンロードすると、ここに表示されます
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-hidden">
          <GridLayout length={products.length} colSize={3} aspectRatio="portrait" componentTemplate={renderItem} />
        </div>
      )}

      {selectedProduct && (
        <EpisodeDrawer
          open={drawerOpen}
          onOpenChange={handleDrawerClose}
          episodes={selectedProduct.episodes}
          lastEpisodeId={null}
          productTitle={selectedProduct.title}
          onEpisodeSelect={handleEpisodeSelect}
          domain={selectedProduct.domain}
          productId={selectedProduct.product_id}
          onRefresh={loadEpisodes}
        />
      )}
    </div>
  )
}
